// BuildingCodesPanel.tsx
// Displays IECC climate zone, state building code adoptions, and prescriptive
// envelope values for a given project address.
//
// Jurisdiction lookup — two-stage:
//   1. US Census Bureau Geocoder (census.gov/geocoder) — uses actual TIGER/Line
//      civil boundary data, so it correctly identifies civil townships, boroughs,
//      and incorporated places (the real code-enforcement jurisdiction).
//   2. Nominatim fallback — used only when Census can't match the address
//      (e.g. very new construction, rural, or incomplete address entry).

import React, { useEffect, useRef, useState } from "react";
import {
  getClimateZone,
  STATE_CODES,
  IECC_ENVELOPE,
  type IECCEnvelopeRow,
} from "../lib/buildingCodes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface JurisdictionResult {
  cz: string;
  municipality: string;
  county: string;
  stateName: string;
  stateCode: string;
  ibc?: string;
  irc?: string;
  iecc?: string;
  ipc?: string;
  imc?: string;
  codesNotes?: string;
}

interface Props {
  address: string;
  /** City+ST+ZIP fallback query from AddressFields */
  geoQuery?: string;
  T: any;
  /** Fires once when the geocode + codes lookup resolves successfully */
  onResult?: (info: JurisdictionResult) => void;
}

interface GeoResult {
  cz: string;
  /** Civil township / incorporated place — the actual code-enforcement authority */
  municipality: string;
  county: string;
  stateName: string;
  stateCode: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

function moistureLabel(cz: string): string {
  if (cz.endsWith("A")) return "Moist";
  if (cz.endsWith("B")) return "Dry";
  if (cz.endsWith("C")) return "Marine";
  return "";
}

/** Title-case a Census place name: "Tredyffrin township" → "Tredyffrin Township" */
function titleCase(s: string): string {
  return s.replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** Parse "123 Main St, City, ST 12345" into structured parts for Census API. */
function parseAddressParts(addr: string): { street: string; city: string; state: string; zip: string } | null {
  // Full format: "123 Main St, City, ST 12345"
  const m = addr.match(/^(.*?),\s*(.*?),?\s*([A-Z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/);
  if (m) return { street: m[1].trim(), city: m[2].trim(), state: m[3], zip: m[4] };
  // No zip: "123 Main St, City, ST"
  const m2 = addr.match(/^(.*?),\s*(.*?),?\s*([A-Z]{2})\s*$/);
  if (m2) return { street: m2[1].trim(), city: m2[2].trim(), state: m2[3], zip: "" };
  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function BuildingCodesPanel({ address, geoQuery, T, onResult }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [result, setResult]   = useState<GeoResult | null>(null);
  const lastGeocodedRef       = useRef<string>("");
  const abortRef              = useRef<AbortController | null>(null);
  const debounceRef           = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    // Full street address is required for Census to resolve the correct
    // civil township (county subdivision). geoQuery (city+ZIP only) is a
    // last resort — Census will fall through to Nominatim for that.
    const effective = address.trim() || (geoQuery ?? "").trim();
    if (!effective) return;

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (effective === lastGeocodedRef.current) return;

      abortRef.current?.abort();
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;

      lastGeocodedRef.current = effective;
      setLoading(true);
      setError(null);

      try {
        // ── Stage 1: Nominatim forward geocode ───────────────────────────────
        // Gets lat/lon, county, state, and a rough municipality. Reliable and
        // always CORS-friendly. For addresses where OSM doesn't carry the
        // township boundary polygon (e.g. Tredyffrin Township), the municipality
        // will be a nearby place name — corrected in Stage 2 below.
        const nomRes = await fetch(
          `https://nominatim.openstreetmap.org/search` +
          `?q=${encodeURIComponent(effective)}&format=json&addressdetails=1&limit=1&countrycodes=us&email=openestimate%40app`,
          { signal, headers: { Accept: "application/json" } }
        );
        if (!nomRes.ok) throw new Error(`HTTP ${nomRes.status}`);
        const nomData: any[] = await nomRes.json();

        if (!nomData || nomData.length === 0) {
          setError("Location not found — check the address and try again.");
          setLoading(false);
          return;
        }

        const fwd            = nomData[0];
        const a              = fwd.address ?? {};
        const rawSC: string  = a.state_code ?? a["ISO3166-2-lvl4"] ?? "";
        const stateCode      = rawSC.replace(/^US-/i, "").toUpperCase();
        const county         = a.county ?? a.state_district ?? "";
        const stateName      = a.state ?? stateCode;
        const cz             = getClimateZone(stateCode, county);
        const lat            = parseFloat(fwd.lat);
        const lon            = parseFloat(fwd.lon);

        let municipality: string =
          a.city ?? a.town ?? a.municipality ??
          a.city_district ?? a.borough ?? a.village ?? "";

        // ── Stage 2: Census jurisdiction lookup (via serverless proxy) ──────────
        // The Census Bureau geocoder uses TIGER/Line civil-boundary data so it
        // correctly identifies the exact township or borough (the real building-
        // permit authority) for any parcel. geocoding.geo.census.gov blocks
        // browser CORS, so we call our own /api/utils proxy which
        // makes the request server-side and returns the clean result.
        if (!isNaN(lat) && !isNaN(lon)) {
          try {
            const proxyRes = await fetch(
              `/api/utils?action=census&lat=${lat}&lon=${lon}`,
              { signal }
            );
            if (proxyRes.ok) {
              const proxyData = await proxyRes.json();
              if (proxyData.municipality) {
                municipality = titleCase(proxyData.municipality);
              }
            }
          } catch (censusErr: any) {
            if (censusErr.name === "AbortError") return;
            // Proxy unavailable (e.g. local dev without Vercel CLI) — keep Nominatim value
            console.warn("[BuildingCodes] census proxy unavailable:", censusErr.message);
          }
        }

        const resolved: GeoResult = { cz, municipality, county, stateName, stateCode };
        setResult(resolved);

        // Fire onResult callback so parent can persist jurisdiction for other panels
        if (onResult) {
          const stateCodes = STATE_CODES[stateCode];
          onResult({
            cz, municipality, county, stateName, stateCode,
            ibc: stateCodes?.ibc,
            irc: stateCodes?.irc,
            iecc: stateCodes?.iecc,
            ipc: stateCodes?.ipc,
            imc: stateCodes?.imc,
            codesNotes: stateCodes?.notes,
          });
        }

        setLoading(false);

      } catch (err: any) {
        if (err.name === "AbortError") return;
        setError(`Could not determine jurisdiction: ${err.message}`);
        setLoading(false);
      }
    }, 900);

    return () => {
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [address, geoQuery]);

  // --------------------------------------------------------------------------
  // Derived data
  // --------------------------------------------------------------------------
  const codes    = result ? STATE_CODES[result.stateCode] : null;
  const envelope = result ? (IECC_ENVELOPE[result.cz] ?? null) : null as IECCEnvelopeRow | null;
  const moisture = result ? moistureLabel(result.cz) : "";

  // --------------------------------------------------------------------------
  // Style helpers
  // --------------------------------------------------------------------------
  const sectionHeaderStyle: React.CSSProperties = {
    fontFamily: FONT, fontSize: 10, fontWeight: 700,
    letterSpacing: "0.08em", textTransform: "uppercase",
    color: T.textMuted, marginBottom: 8, marginTop: 0,
  };
  const cardStyle: React.CSSProperties = {
    background: T.bgApp, border: `1px solid ${T.borderSecondary}`,
    borderRadius: 6, padding: "14px 16px", marginBottom: 16,
  };
  const tableStyle: React.CSSProperties = {
    width: "100%", borderCollapse: "collapse", fontFamily: FONT, fontSize: 13,
  };
  const thStyle: React.CSSProperties = {
    textAlign: "left", padding: "5px 8px", fontWeight: 600,
    fontSize: 11, letterSpacing: "0.05em", textTransform: "uppercase",
    color: T.textMuted, borderBottom: `1px solid ${T.borderSecondary}`,
    background: "transparent",
  };
  const tdLabel: React.CSSProperties = {
    padding: "6px 8px", color: T.textSecondary, fontFamily: FONT,
    fontSize: 13, borderBottom: `1px solid ${T.borderSecondary}`, whiteSpace: "nowrap",
  };
  const tdValue: React.CSSProperties = {
    padding: "6px 8px", color: T.textSecondary, fontFamily: FONT,
    fontSize: 13, borderBottom: `1px solid ${T.borderSecondary}`,
  };
  const noteStyle: React.CSSProperties = {
    fontFamily: FONT, fontSize: 11, color: T.textWeak ?? T.textMuted,
    marginTop: 8, lineHeight: 1.5,
  };
  const disclaimerStyle: React.CSSProperties = {
    fontFamily: FONT, fontSize: 11, color: T.textWeak ?? T.textMuted,
    marginTop: 10, lineHeight: 1.5, fontStyle: "italic",
  };

  const hasAddress = !!(address.trim() || (geoQuery ?? "").trim());

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div style={{ fontFamily: FONT, color: T.textSecondary }}>

      {/* ── Climate Zone ──────────────────────────────────────────────────── */}
      <p style={sectionHeaderStyle}>Climate Zone</p>
      <div style={cardStyle}>
        {!hasAddress ? (
          <p style={{ margin: 0, fontFamily: FONT, fontSize: 13, color: T.textWeak ?? T.textMuted, lineHeight: 1.6 }}>
            Enter a project address in Project Info above to look up the applicable building codes.
          </p>
        ) : loading ? (
          <p style={{ margin: 0, fontFamily: FONT, fontSize: 13, color: T.textWeak ?? T.textMuted, fontStyle: "italic" }}>
            Determining climate zone…
          </p>
        ) : error ? (
          <p style={{ margin: 0, fontFamily: FONT, fontSize: 13, color: "#e05c5c" }}>{error}</p>
        ) : result ? (
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            {/* Badge */}
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", minWidth: 72, minHeight: 72,
              border: `2px solid ${T.borderAccent ?? "#5b9bd5"}`, borderRadius: 10,
              padding: "8px 12px", flexShrink: 0,
            }}>
              <span style={{ fontFamily: FONT, fontSize: 32, fontWeight: 700, lineHeight: 1,
                color: T.borderAccent ?? "#5b9bd5", letterSpacing: "-0.01em" }}>
                {result.cz}
              </span>
              {moisture && (
                <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.06em", textTransform: "uppercase",
                  color: T.textMuted, marginTop: 4 }}>
                  {moisture}
                </span>
              )}
            </div>
            {/* Location info */}
            <div>
              {result.municipality && (
                <div style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600,
                  color: T.textSecondary, marginBottom: 2 }}>
                  {result.municipality}
                </div>
              )}
              {result.county && (
                <div style={{ fontFamily: FONT, fontSize: 13, color: T.textMuted, marginBottom: 2 }}>
                  {result.county}
                </div>
              )}
              <div style={{ fontFamily: FONT, fontSize: 13, color: T.textMuted }}>
                {result.stateName}
              </div>
              <div style={{ fontFamily: FONT, fontSize: 12, color: T.textWeak ?? T.textMuted, marginTop: 6 }}>
                IECC Climate Zone {result.cz}{moisture ? ` — ${moisture}` : ""}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* ── Jurisdiction ──────────────────────────────────────────────────── */}
      {result && (
        <>
          <p style={{ ...sectionHeaderStyle, marginTop: 4 }}>Jurisdiction</p>
          <div style={cardStyle}>
            <table style={tableStyle}>
              <tbody>
                {result.municipality && (
                  <tr>
                    <td style={tdLabel}>Municipality</td>
                    <td style={tdValue}>{result.municipality}</td>
                  </tr>
                )}
                {result.county && (
                  <tr>
                    <td style={tdLabel}>County</td>
                    <td style={tdValue}>{result.county}</td>
                  </tr>
                )}
                <tr>
                  <td style={tdLabel}>State</td>
                  <td style={tdValue}>{result.stateName}</td>
                </tr>
                {(result.municipality || result.county) && (
                  <tr>
                    <td style={{ ...tdLabel, borderBottom: "none" }}>Code Enforcement</td>
                    <td style={{ ...tdValue, borderBottom: "none", fontStyle: "italic",
                      color: T.textWeak ?? T.textMuted }}>
                      {result.municipality || result.county} Building &amp; Zoning Department
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Applicable Codes ──────────────────────────────────────────────── */}
      {result && codes && (
        <>
          <p style={{ ...sectionHeaderStyle, marginTop: 4 }}>Applicable Codes</p>
          <div style={cardStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Code Type</th>
                  <th style={thStyle}>Adopted Edition</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={tdLabel}>Commercial (IBC)</td><td style={tdValue}>{codes.ibc}</td></tr>
                <tr><td style={tdLabel}>Residential (IRC)</td><td style={tdValue}>{codes.irc}</td></tr>
                <tr><td style={tdLabel}>Energy (IECC)</td><td style={tdValue}>{codes.iecc}</td></tr>
                <tr><td style={tdLabel}>Plumbing (IPC)</td><td style={tdValue}>{codes.ipc}</td></tr>
                <tr>
                  <td style={{ ...tdLabel, borderBottom: "none" }}>Mechanical (IMC)</td>
                  <td style={{ ...tdValue, borderBottom: "none" }}>{codes.imc}</td>
                </tr>
              </tbody>
            </table>
            {codes.notes && <p style={noteStyle}>Note: {codes.notes}</p>}
          </div>
        </>
      )}

      {/* ── IECC Prescriptive Envelope ────────────────────────────────────── */}
      {result && envelope && (
        <>
          <p style={{ ...sectionHeaderStyle, marginTop: 4 }}>
            IECC Prescriptive Envelope&nbsp;
            <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
              — Zone {result.cz}
            </span>
          </p>
          <div style={cardStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Assembly</th>
                  <th style={thStyle}>Requirement</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={tdLabel}>Fenestration (Windows) U-factor</td><td style={tdValue}>{envelope.fenestrationU}</td></tr>
                <tr><td style={tdLabel}>Skylight U-factor</td><td style={tdValue}>{envelope.skylightU}</td></tr>
                <tr><td style={tdLabel}>Solar Heat Gain (SHGC)</td><td style={tdValue}>{envelope.shgc}</td></tr>
                <tr><td style={tdLabel}>Ceiling / Attic</td><td style={tdValue}>{envelope.ceilingR}</td></tr>
                <tr><td style={tdLabel}>Wood-Frame Wall</td><td style={tdValue}>{envelope.wfWallR}</td></tr>
                <tr><td style={tdLabel}>Mass Wall</td><td style={tdValue}>{envelope.massWallR}</td></tr>
                <tr><td style={tdLabel}>Floor</td><td style={tdValue}>{envelope.floorR}</td></tr>
                <tr><td style={tdLabel}>Basement Wall</td><td style={tdValue}>{envelope.basementR}</td></tr>
                <tr><td style={tdLabel}>Slab Edge</td><td style={tdValue}>{envelope.slabR}</td></tr>
                <tr>
                  <td style={{ ...tdLabel, borderBottom: "none" }}>Crawl Space Wall</td>
                  <td style={{ ...tdValue, borderBottom: "none" }}>{envelope.crawlR}</td>
                </tr>
              </tbody>
            </table>
            <p style={disclaimerStyle}>
              Values from 2021 IECC Table R402.1.2 (residential prescriptive). Verify against the
              locally adopted code edition and amendments.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default BuildingCodesPanel;
